// src/components/FAQ.jsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

// Exemple d'icône pour illustrer chaque question.
const QuestionIcon = () => (
  <svg
    className="w-6 h-6 mr-2 text-primary shrink-0"
    fill="currentColor"
    viewBox="0 0 20 20"
  >
    <path d="M18 10c0 4.418-3.582 8-8 8S2 14.418 2 10 5.582 2 10 2s8 3.582 8 8zm-8-3a1 1 0 00-1 1v.5a1 1 0 001 1 1 1 0 011 1v.5a1 1 0 01-2 0H8a2 2 0 002 2v1a1 1 0 102 0v-1a2 2 0 00-1-1.732V8a1 1 0 00-1-1z" />
  </svg>
);

const FAQ = () => {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState(null);

  // Récupérer les questions et réponses depuis les traductions
  // IMPORTANT: Ensure that the translated content for 'faq.answers.*' keys,
  // especially for questions about certainty, effectiveness (e.g., 'faq.answers.2.*', 'faq.answers.4.*'),
  // and benefits like 'faq.answers.4.benefits.calculate',
  // strictly adheres to the Terms of Service. Answers should emphasize information,
  // estimation, and guidance, and must not constitute legal advice or guarantee outcomes.
  // For example, any "calculate" benefit should be framed as "estimation tool".
  const faqData = t('faq.questions', { returnObjects: true }) || [];

  // Fonction pour rendre la réponse avec le bon format selon la question
  const renderAnswer = (question, index) => {
    // Question 1: Est-ce que je peux vraiment trouver...
    if (index === 0) {
      return (
        <>
          <p className="mb-4 font-semibold text-primary">
            {t('faq.answers.1.header')}
          </p>
          <p className="mb-4">
            {t('faq.answers.1.intro')}
          </p>
          <div className="flex items-start mt-4 bg-primary/5 p-4 rounded-lg">
            <span className="text-primary mr-2">👉</span>
            <p className="italic">
              {t('faq.answers.1.resources')}
            </p>
          </div>
          <div className="mt-4 p-4 bg-warning/10 rounded-lg">
            <p className="text-sm">
              <span className="font-semibold">{t('faq.answers.1.approach.title')}</span>{' '}
              {t('faq.answers.1.approach.content')}
            </p>
          </div>
        </>
      );
    }

    // Question 2: Comment être sûr...
    if (index === 1) {
      return (
        <div className="space-y-4">
          <p className="font-semibold text-primary">
            {t('faq.answers.2.title')}
          </p>
          <p className="bg-primary/5 p-4 rounded-lg">
            {t('faq.answers.2.content')}
          </p>
        </div>
      );
    }

    // Question 3: Est-ce que myPlum+ remplace...
    if (index === 2) {
      return (
        <>
          <p className="mb-4 font-bold text-xl text-primary">{t('faq.answers.3.never')}</p>
          <div className="bg-primary/5 p-4 rounded-lg mb-4">
            <p className="font-semibold mb-2">{t('faq.answers.3.when')}</p>
            <p>{t('faq.answers.3.cases')}</p>
          </div>
          <p className="italic">
            {t('faq.answers.3.preparation')}
          </p>
        </>
      );
    }

    // Question 4: Ça marche vraiment...
    if (index === 3) {
      return (
        <>
          <p className="mb-4">
            {t('faq.answers.4.intro')}
          </p>
          <div className="bg-primary/5 p-4 rounded-lg">
            <p className="font-semibold mb-2">{t('faq.answers.4.forYou')}</p>
            <ul className="list-disc ml-4 space-y-2">
              <li>
                <span className="text-primary font-medium">{t('faq.answers.4.benefits.calculate')}</span>
              </li>
              <li>
                <span className="text-primary font-medium">{t('faq.answers.4.benefits.guide')}</span>
              </li>
              <li>
                <span className="text-primary font-medium">{t('faq.answers.4.benefits.save')}</span>
              </li>
              <li>
                <span className="text-primary font-medium">{t('faq.answers.4.benefits.understand')}</span>
              </li>
            </ul>
          </div>
          <div className="flex items-start mt-4 bg-warning/10 p-4 rounded-lg">
            <span className="text-warning mr-2">💡</span>
            <p className="italic">
              {t('faq.answers.4.savings')}
            </p>
          </div>
        </>
      );
    }

    // Question 5: Est-ce que cela restera gratuit ?
    if (index === 4) {
      return (
        <div className="space-y-4">
          <div className="bg-primary/5 p-4 rounded-lg">
            <p className="font-bold text-lg mb-2">{t('faq.answers.5.basic.title')}</p>
            <p>{t('faq.answers.5.basic.content')}</p>
          </div>
          <div className="bg-gray-800 text-white p-4 rounded-lg">
            <p className="font-bold text-lg mb-2">{t('faq.answers.5.pro.title')}</p>
            <p>{t('faq.answers.5.pro.content')}</p>
          </div>
          <p className="italic font-medium text-primary">
            {t('faq.answers.5.summary')}
          </p>
        </div>
      );
    }

    // Question 6: Est-ce que le paiement est sécurisé
    if (index === 5) {
      return (
        <div className="bg-primary/5 p-4 rounded-lg">
          <p className="font-semibold mb-2">
            {t('faq.answers.6.security')}
          </p>
          <p>
            {t('faq.answers.6.privacy')}
          </p>
        </div>
      );
    }

    // Question 7: Est-ce qu'il y a un vrai humain
    if (index === 6) {
      return (
        <div className="space-y-4">
          <p className="font-bold text-lg text-primary">
            {t('faq.answers.7.affirmative')}
          </p>
          <div className="bg-primary/5 p-4 rounded-lg">
            <p>
              {t('faq.answers.7.description')}
            </p>
          </div>
        </div>
      );
    }

    // Question 8: Et si finalement, je réalise que j'ai besoin d'un avocat
    if (index === 7) {
      return (
        <div className="space-y-4">
          <p className="font-bold text-lg text-primary">
            {t('faq.answers.8.intro')}
          </p>
          <div className="bg-primary/5 p-4 rounded-lg">
            <p className="mb-4">
              {t('faq.answers.8.explanation')}
            </p>
            <ul className="list-disc ml-4 space-y-2 mb-4">
              {t('faq.answers.8.benefits', { returnObjects: true }).map((benefit, idx) => (
                <li key={idx}>
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
            <p className="italic text-primary font-medium">
              {t('faq.answers.8.summary')}
            </p>
          </div>
        </div>
      );
    }

    // Pour les autres questions, retourner une réponse simple
    return <p className="text-gray-600">{question.answer}</p>;
  };

  return (
    <section className="py-12 md:py-16 bg-gray-50 relative">
      {/* If there are any decorative elements, make them non-interactive */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Decorative elements would go here */}
      </div>
      
      <div className="max-w-4xl mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-extrabold mb-4">
            {t('faq.title')} <span className="text-primary">myPlum+</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t('faq.subtitle')}
          </p>
        </div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {faqData.map((item, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl shadow-md relative overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left transition-colors hover:bg-gray-50 relative z-20"
                aria-expanded={openIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                <h3 className="font-semibold text-lg">{item.question}</h3>
                <span 
                  className="text-primary text-2xl transform transition-transform duration-300 ml-4" 
                  style={{
                    transform: openIndex === index ? 'rotate(180deg)' : 'rotate(0)'
                  }}
                >
                  ▼
                </span>
              </button>

              <div 
                id={`faq-answer-${index}`}
                className={`overflow-hidden transition-all duration-300 ease-in-out relative z-10 ${
                  openIndex === index ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="px-6 pb-6">
                  {renderAnswer(item, index)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ;
